import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, update } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  UserIcon, EnvelopeIcon, PhoneIcon, KeyIcon,
  QrCodeIcon, CameraIcon, CheckCircleIcon,
  PencilIcon, ShieldCheckIcon, CalendarIcon,
  XMarkIcon, EyeIcon, EyeSlashIcon,
  ExclamationTriangleIcon, ArrowPathIcon,
  SparklesIcon, CheckIcon, IdentificationIcon,
} from '@heroicons/react/24/outline';
import { updatePassword, updateEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../../firebase';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PRIMARY = '#800020';
const DARK    = '#1a1a2e';

const SWAL_Z = { customClass: { container: 'prof-swal-z' } };
if (typeof document !== 'undefined' && !document.getElementById('prof-swal-z-style')) {
  const s = document.createElement('style');
  s.id = 'prof-swal-z-style';
  s.textContent = '.prof-swal-z{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' });
};

const fmtDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getMembershipBadge = (status, expiryTs) => {
  const days = expiryTs ? Math.ceil((expiryTs - Date.now()) / 86400000) : null;
  if (status === 'active') {
    if (days !== null && days <= 7) return { label: `Expires in ${days}d`, color: '#d97706', bg: '#fffbeb', dot: '#f59e0b' };
    return { label: 'Active', color: '#16a34a', bg: '#f0fdf4', dot: '#22c55e' };
  }
  if (status === 'expired') return { label: 'Expired',  color: '#dc2626', bg: '#fef2f2', dot: '#ef4444' };
  if (status === 'pending') return { label: 'Pending',  color: '#2563eb', bg: '#eff6ff', dot: '#3b82f6' };
  return                           { label: 'Inactive', color: '#6b7280', bg: '#f9fafb', dot: '#9ca3af' };
};

// password strength
const getStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: '#e5e7eb' };
  let score = 0;
  if (pw.length >= 6)          score++;
  if (/[A-Z]/.test(pw))        score++;
  if (/[a-z]/.test(pw))        score++;
  if (/[0-9]/.test(pw))        score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: 'Very Weak',   color: '#ef4444' },
    { label: 'Weak',        color: '#f97316' },
    { label: 'Fair',        color: '#eab308' },
    { label: 'Good',        color: '#3b82f6' },
    { label: 'Strong',      color: '#22c55e' },
    { label: 'Very Strong', color: PRIMARY   },
  ];
  return { score, ...map[score] };
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Profile = () => {
  const { userData, currentUser } = useAuth();

  const [editPersonalOpen, setEditPersonalOpen] = useState(false);
  const [editPasswordOpen, setEditPasswordOpen] = useState(false);

  const pi         = userData?.personalInfo || {};
  const membership = userData?.membership   || {};
  const wallet     = userData?.wallet       || {};
  const mb         = getMembershipBadge(membership.status, membership.expiryDate);

  const daysLeft = membership.expiryDate
    ? Math.max(0, Math.ceil((membership.expiryDate - Date.now()) / 86400000))
    : null;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#f0f0f0 100%)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

        {/* ── HERO PROFILE CARD ── */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative rounded-3xl overflow-hidden mb-6 shadow-xl">

          {/* Background gradient */}
          <div className="h-32 w-full" style={{ background: `linear-gradient(135deg,${DARK} 0%,${PRIMARY} 60%,#c0392b 100%)` }}>
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
              style={{ background: 'white', transform: 'translate(30%,-30%)' }} />
            <div className="absolute top-0 right-20 w-32 h-32 rounded-full opacity-10"
              style={{ background: 'white', transform: 'translate(0,-50%)' }} />
          </div>

          <div className="bg-white px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10 sm:-mt-12">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                {pi.facePhotoUrl ? (
                  <img src={pi.facePhotoUrl} alt="Profile"
                    className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg" />
                ) : (
                  <div className="w-24 h-24 rounded-2xl flex items-center justify-center text-white font-black text-3xl border-4 border-white shadow-lg"
                    style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
                    {(pi.name || 'U')[0].toUpperCase()}
                  </div>
                )}
                {pi.facePhotoUrl && (
                  <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-green-500 border-2 border-white flex items-center justify-center">
                    <CheckCircleIcon className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>

              {/* Name + role */}
              <div className="flex-1 pb-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-black text-gray-900">{pi.name || 'Member'}</h1>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full capitalize"
                    style={{ background: `${PRIMARY}15`, color: PRIMARY }}>
                    {pi.role || 'user'}
                  </span>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                    style={{ background: mb.bg, color: mb.color }}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                      style={{ background: mb.dot }} />
                    {mb.label}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mt-1">{pi.email}</p>
              </div>

              {/* Edit button */}
              <button onClick={() => setEditPersonalOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all hover:scale-105 self-end sm:self-auto"
                style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
                <PencilIcon className="h-4 w-4" />
                Edit Profile
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── STATS ROW ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            {
              label: 'Wallet Balance',
              val:   `KES ${(wallet.balance || 0).toLocaleString()}`,
              icon:  '💰',
              color: '#16a34a',
              bg:    '#f0fdf4',
            },
            {
              label: 'Membership',
              val:   membership.status ? membership.status.charAt(0).toUpperCase() + membership.status.slice(1) : 'Inactive',
              icon:  '🏋️',
              color: mb.color,
              bg:    mb.bg,
            },
            {
              label: 'Days Left',
              val:   daysLeft !== null ? `${daysLeft} days` : '—',
              icon:  '📅',
              color: daysLeft !== null && daysLeft <= 7 ? '#dc2626' : '#2563eb',
              bg:    daysLeft !== null && daysLeft <= 7 ? '#fef2f2' : '#eff6ff',
            },
            {
              label: 'Face Scan',
              val:   pi.facePhotoUrl ? 'Active' : 'Not Set',
              icon:  '👤',
              color: pi.facePhotoUrl ? '#16a34a' : '#d97706',
              bg:    pi.facePhotoUrl ? '#f0fdf4' : '#fffbeb',
            },
          ].map((stat, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.06 }}
              className="rounded-2xl p-4 border shadow-sm"
              style={{ background: stat.bg, borderColor: `${stat.color}25` }}>
              <p className="text-2xl mb-2">{stat.icon}</p>
              <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
              <p className="font-black text-base mt-0.5" style={{ color: stat.color }}>{stat.val}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

          {/* ── PERSONAL INFO CARD ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <UserIcon className="h-5 w-5" style={{ color: PRIMARY }} />
                <h2 className="font-bold text-gray-900">Personal Info</h2>
              </div>
              <button onClick={() => setEditPersonalOpen(true)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
                style={{ color: PRIMARY, background: `${PRIMARY}10` }}>
                Edit
              </button>
            </div>
            <div className="p-5 space-y-4">
              {[
                { label: 'Full Name', val: pi.name,   icon: <UserIcon       className="h-4 w-4" /> },
                { label: 'Email',     val: pi.email,  icon: <EnvelopeIcon   className="h-4 w-4" /> },
                { label: 'Phone',     val: pi.phone,  icon: <PhoneIcon      className="h-4 w-4" /> },
                { label: 'RFID Card', val: pi.rfidCard || 'Not assigned', icon: <QrCodeIcon className="h-4 w-4" /> },
                { label: 'Joined',    val: fmtDate(pi.registeredAt), icon: <CalendarIcon className="h-4 w-4" /> },
                { label: 'Provider',  val: pi.provider, icon: <KeyIcon      className="h-4 w-4" /> },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${PRIMARY}10`, color: PRIMARY }}>
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">{item.label}</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{item.val || '—'}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── ACCOUNT SECURITY CARD ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5" style={{ color: PRIMARY }} />
                <h2 className="font-bold text-gray-900">Account Security</h2>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Email verified */}
              <div className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: pi.emailVerified ? '#f0fdf4' : '#fef2f2' }}>
                {pi.emailVerified
                  ? <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  : <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                }
                <div>
                  <p className="text-sm font-bold" style={{ color: pi.emailVerified ? '#16a34a' : '#dc2626' }}>
                    Email {pi.emailVerified ? 'Verified' : 'Not Verified'}
                  </p>
                  <p className="text-xs text-gray-500">{pi.email}</p>
                </div>
              </div>

              {/* Last login */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${PRIMARY}10`, color: PRIMARY }}>
                  <ClockIconSmall />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Last Login</p>
                  <p className="text-sm font-semibold text-gray-800">{fmtDateTime(pi.lastLogin)}</p>
                </div>
              </div>

              {/* Face scan */}
              <div className="flex items-center gap-3 p-3 rounded-xl border"
                style={{ background: pi.facePhotoUrl ? '#f0fdf4' : '#fffbeb', borderColor: pi.facePhotoUrl ? '#bbf7d0' : '#fde68a' }}>
                <CameraIcon className="h-5 w-5 flex-shrink-0" style={{ color: pi.facePhotoUrl ? '#16a34a' : '#d97706' }} />
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: pi.facePhotoUrl ? '#16a34a' : '#d97706' }}>
                    Face Scan {pi.facePhotoUrl ? 'Active' : 'Not Set Up'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {pi.facePhotoUrl ? `Set up on ${fmtDate(pi.faceSetupAt)}` : 'Set up from your dashboard for easy gym access'}
                  </p>
                </div>
              </div>

              {/* Change password */}
              {pi.provider === 'email' && (
                <button onClick={() => setEditPasswordOpen(true)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-dashed text-left transition-all hover:shadow-sm"
                  style={{ borderColor: `${PRIMARY}30`, color: PRIMARY }}
                  onMouseEnter={e => e.currentTarget.style.background = `${PRIMARY}05`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <KeyIcon className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold">Change Password</p>
                    <p className="text-xs text-gray-400">Update your account password</p>
                  </div>
                </button>
              )}
            </div>
          </motion.div>

          {/* ── MEMBERSHIP CARD (read-only) ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sm:col-span-2">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
              <SparklesIcon className="h-5 w-5" style={{ color: PRIMARY }} />
              <h2 className="font-bold text-gray-900">Membership Details</h2>
              <span className="ml-auto text-xs text-gray-400 italic">Managed by gym staff</span>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Status',    val: membership.status    || 'Inactive', highlight: true },
                  { label: 'Package',   val: membership.packageName || membership.planKey || '—' },
                  { label: 'Start Date', val: fmtDate(membership.startDate) },
                  { label: 'Expiry',    val: fmtDate(membership.expiryDate) },
                ].map((item, i) => (
                  <div key={i} className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-xs text-gray-400">{item.label}</p>
                    <p className="font-bold text-sm mt-0.5 capitalize"
                      style={{ color: item.highlight ? mb.color : '#1a1a2e' }}>
                      {item.val}
                    </p>
                  </div>
                ))}
              </div>
              {membership.status !== 'active' && (
                <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">
                    Your membership is not active. Visit reception or contact us to renew your membership and regain full gym access.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── EDIT PERSONAL MODAL ── */}
      <AnimatePresence>
        {editPersonalOpen && (
          <EditPersonalModal
            userData={userData}
            currentUser={currentUser}
            onClose={() => setEditPersonalOpen(false)}
            onSaved={() => setEditPersonalOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* ── CHANGE PASSWORD MODAL ── */}
      <AnimatePresence>
        {editPasswordOpen && (
          <ChangePasswordModal
            currentUser={currentUser}
            onClose={() => setEditPasswordOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// small clock icon inline
const ClockIconSmall = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT PERSONAL MODAL — full screen
// ═══════════════════════════════════════════════════════════════════════════════
const EditPersonalModal = ({ userData, currentUser, onClose, onSaved }) => {
  const pi = userData?.personalInfo || {};

  const [name,  setName]  = useState(pi.name  || '');
  const [phone, setPhone] = useState(pi.phone || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return toast('error', 'Name required', 'Please enter your full name');
    setSaving(true);
    try {
      await update(ref(database, `users/${currentUser.uid}/personalInfo`), {
        name:  name.trim(),
        phone: phone.trim(),
      });
      toast('success', 'Profile updated!');
      onSaved();
    } catch (err) {
      console.error(err);
      toast('error', 'Failed to save', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
          <div>
            <h2 className="text-xl font-bold text-white">Edit Personal Info</h2>
            <p className="text-white/60 text-sm mt-0.5">Update your name and phone number</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-8" style={{ minHeight: 0 }}>
          <div className="max-w-xl mx-auto space-y-6">

            <ProfSection title="Basic Information">
              <div className="space-y-4">
                <ProfField label="Full Name *" value={name} onChange={setName} placeholder="John Kamau"
                  icon={<UserIcon className="h-4 w-4" />} />
                <ProfField label="Phone Number" value={phone} onChange={setPhone} placeholder="+254 712 345 678"
                  type="tel" icon={<PhoneIcon className="h-4 w-4" />} />
              </div>
            </ProfSection>

            <ProfSection title="Read-Only Info">
              <div className="space-y-3">
                {[
                  { label: 'Email Address (cannot change)', val: pi.email },
                  { label: 'RFID Card (assigned by gym)',   val: pi.rfidCard || 'Not assigned' },
                  { label: 'Account Role',                  val: pi.role },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex-1">
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-sm font-semibold text-gray-700 mt-0.5">{item.val || '—'}</p>
                    </div>
                    <span className="text-xs text-gray-400 italic">locked</span>
                  </div>
                ))}
              </div>
            </ProfSection>

            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
              <p className="text-sm text-blue-700 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-500" />
                Membership details and wallet balance are managed by gym staff. Contact reception for any changes.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-10 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
            style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
            {saving
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
              : <><CheckIcon className="h-4 w-4" /> Save Changes</>
            }
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CHANGE PASSWORD MODAL — full screen
// ═══════════════════════════════════════════════════════════════════════════════
const ChangePasswordModal = ({ currentUser, onClose }) => {
  const [current,  setCurrent]  = useState('');
  const [newPw,    setNewPw]    = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showCur,  setShowCur]  = useState(false);
  const [showNew,  setShowNew]  = useState(false);
  const [showCon,  setShowCon]  = useState(false);
  const [saving,   setSaving]   = useState(false);

  const strength  = getStrength(newPw);
  const matches   = newPw && confirm ? newPw === confirm : null;

  const handleSave = async () => {
    if (!current) return toast('error', 'Current password required');
    if (newPw.length < 6) return toast('error', 'Password too short', 'Minimum 6 characters');
    if (!matches) return toast('error', 'Passwords do not match');

    setSaving(true);
    try {
      // Re-authenticate first
      const credential = EmailAuthProvider.credential(currentUser.email, current);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPw);
      toast('success', 'Password changed!', 'Your password has been updated');
      onClose();
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/wrong-password') toast('error', 'Wrong current password');
      else toast('error', 'Failed to change password', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
          <div>
            <h2 className="text-xl font-bold text-white">Change Password</h2>
            <p className="text-white/60 text-sm mt-0.5">Update your account password securely</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-8" style={{ minHeight: 0 }}>
          <div className="max-w-xl mx-auto space-y-6">

            <ProfSection title="Current Password">
              <PasswordField label="Current Password *" value={current} onChange={setCurrent}
                show={showCur} onToggle={() => setShowCur(v => !v)} placeholder="Enter current password" />
            </ProfSection>

            <ProfSection title="New Password">
              <div className="space-y-4">
                <div>
                  <PasswordField label="New Password *" value={newPw} onChange={setNewPw}
                    show={showNew} onToggle={() => setShowNew(v => !v)} placeholder="Enter new password" />
                  {newPw && (
                    <div className="mt-2 space-y-1">
                      <div className="flex gap-1">
                        {[0,1,2,3,4].map(i => (
                          <div key={i} className="h-1.5 flex-1 rounded-full transition-all"
                            style={{ background: i < strength.score ? strength.color : '#e5e7eb' }} />
                        ))}
                      </div>
                      <p className="text-xs" style={{ color: strength.color }}>{strength.label}</p>
                    </div>
                  )}
                </div>

                <div>
                  <PasswordField label="Confirm New Password *" value={confirm} onChange={setConfirm}
                    show={showCon} onToggle={() => setShowCon(v => !v)} placeholder="Repeat new password"
                    borderColor={matches === false ? '#ef4444' : matches === true ? '#22c55e' : undefined} />
                  {confirm && (
                    <p className="text-xs mt-1 flex items-center gap-1"
                      style={{ color: matches ? '#16a34a' : '#dc2626' }}>
                      {matches ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <XMarkIcon className="h-3.5 w-3.5" />}
                      {matches ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                  )}
                </div>
              </div>
            </ProfSection>

            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-700 flex items-start gap-2">
                <ShieldCheckIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                You will need to enter your current password to confirm the change. Use a strong, unique password.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !matches || !current}
            className="px-10 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
            style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
            {saving
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Changing…</>
              : <><KeyIcon className="h-4 w-4" /> Change Password</>
            }
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── SHARED ───────────────────────────────────────────────────────────────────
const ProfSection = ({ title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <p className="text-base font-bold text-gray-900 whitespace-nowrap">{title}</p>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
    {children}
  </div>
);

const ProfField = ({ label, value, onChange, placeholder, type = 'text', icon }) => (
  <div>
    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">{label}</label>
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }}>{icon}</div>
      )}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
        style={{ paddingLeft: icon ? '2.5rem' : '1rem', paddingRight: '1rem', '--tw-ring-color': PRIMARY }}
      />
    </div>
  </div>
);

const PasswordField = ({ label, value, onChange, show, onToggle, placeholder, borderColor }) => (
  <div>
    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">{label}</label>
    <div className="relative">
      <KeyIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-10 py-2.5 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
        style={{ borderColor: borderColor || '#e5e7eb', '--tw-ring-color': PRIMARY }}
      />
      <button type="button" onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
        {show ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
      </button>
    </div>
  </div>
);

export default Profile;